import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  output,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { NgxMaskDirective } from 'ngx-mask';
import { BsDatepickerModule, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { plLocale } from 'ngx-bootstrap/locale';
import { ReceiptDialogComponent } from './components/receipt-dialog/receipt-dialog.component';
import { ApplicationSaveDto } from '@app/models/application-save-dto';
import { ApplicationFormModel } from '@app/models/application-form-model';
import { ValidateMessageComponent } from '@app/components/validate-message/validate-message.component';
import { environment } from '../../../../../environments/environment';
import * as chance from 'chance';
import { format } from 'date-fns';
import { AnimateComponent } from '@app/components/animate/animate.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PolishValidator } from '@app/validators/polish.validator';
import { GtmClickDirective } from '@app/directives/gtm-click.directive';

defineLocale('pl', plLocale);

@Component({
  selector: 'app-form-section',
  templateUrl: './form-section.component.html',
  styleUrls: ['./form-section.component.scss'],
  standalone: true,
  imports: [
    NgOptimizedImage,
    ReactiveFormsModule,
    CommonModule,
    NgxMaskDirective,
    BsDatepickerModule,
    ValidateMessageComponent,
    AnimateComponent,
    GtmClickDirective,
  ],
})
export class FormSectionComponent {
  @ViewChild('imageResult', { read: ElementRef })
  public imageResult?: ElementRef;

  public readonly isDragOver = signal<boolean>(false);
  public readonly selectedFile = signal<File | null>(null);
  public readonly register = output<{
    dto: ApplicationSaveDto;
    form: FormGroup<ApplicationFormModel>;
  }>();

  public readonly today = new Date();
  private readonly windowStart = new Date(2025, 8, 15);
  private readonly windowEndHardCap = new Date(2025, 10, 15);
  public readonly minDate: Date = this.windowStart;
  public readonly maxDate: Date = new Date(
    Math.min(this.windowEndHardCap.getTime(), this.today.setHours(0,0,0,0))
  );
  public readonly chance = environment.development ? chance.Chance() : null;
  private readonly _toastrService = inject(ToastrService);
  private readonly _bsLocaleService = inject(BsLocaleService);
  private readonly _renderer2 = inject(Renderer2);
  private readonly _formBuilder = inject(FormBuilder);
  public readonly form: FormGroup<ApplicationFormModel> =
    this._formBuilder.group<ApplicationFormModel>({
      firstname: new FormControl<string | null>(this.chance?.first() || null, [
        Validators.required,
        Validators.maxLength(50),
      ]),
      lastname: new FormControl<string | null>(this.chance?.last() || null, [
        Validators.required,
        Validators.maxLength(50),
      ]),
      email: new FormControl<string | null>(this.chance?.email() || null, [
        Validators.required,
        Validators.email,
        Validators.maxLength(50),
      ]),
      mobile: new FormControl<string | null>(
        this.chance?.phone({ formatted: false, country: 'pl', mobile: true }) ||
          null,
        [Validators.required],
      ),
      proofOfPurchaseNumber: new FormControl<string | null>(
        this.chance?.word() || null,
        [Validators.required, Validators.maxLength(50)],
      ),
      dateOfPurchase: new FormControl<Date | null>(
        (this.chance?.date({
          min: this.minDate,
          max: this.maxDate,
        }) as Date) || null,
        [Validators.required, Validators.maxLength(50)],
      ),
      imageOfPurchase: new FormControl<File | null>(null, [
        Validators.required,
      ]),
      consentAdult: new FormControl<boolean | null>(
        this.chance?.bool({ likelihood: 100 }) || null,
        [Validators.requiredTrue],
      ),
      consentRegulations: new FormControl<boolean | null>(
        this.chance?.bool({ likelihood: 100 }) || null,
        [Validators.requiredTrue],
      ),
      consentClause: new FormControl<boolean | null>(
        this.chance?.bool({ likelihood: 100 }) || null,
        [Validators.requiredTrue],
      ),
      consentCommunication: new FormControl<boolean | null>(
        this.chance?.bool({ likelihood: 100 }) || null,
        [Validators.requiredTrue],
      ),
      consentExclusion: new FormControl<boolean | null>(
        this.chance?.bool({ likelihood: 100 }) || null,
        [Validators.requiredTrue],
      ),
      nip: new FormControl<string | null>(this.chance ? '1078267429' : null, [
        Validators.required,
        PolishValidator.nip,
      ]),
    });
  private readonly _ngbModal = inject(NgbModal);

  constructor() {
    this._bsLocaleService.use('pl');
  }

  get f() {
    return this.form.controls;
  }

  public onRegister(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this._toastrService.error(
        'Wypełnij wszystkie wymagane pola formularza!',
        'Błąd',
      );
      return;
    }

    const {
      firstname,
      lastname,
      email,
      mobile,
      imageOfPurchase,
      proofOfPurchaseNumber,
      dateOfPurchase,
      consentAdult,
      consentRegulations,
      consentClause,
      consentCommunication,
      consentExclusion,
      nip,
    } = this.form.value;

    if (
      !firstname ||
      !lastname ||
      !email ||
      !mobile ||
      !imageOfPurchase ||
      !proofOfPurchaseNumber ||
      !dateOfPurchase ||
      !consentAdult ||
      !consentRegulations ||
      !consentClause ||
      !consentCommunication ||
      !consentExclusion ||
      !nip
    ) {
      this._toastrService.error(
        'Wypełnij wszystkie wymagane pola formularza!',
        'Błąd',
      );
      return;
    }

    const dto: ApplicationSaveDto = {
      firstname,
      lastname,
      email,
      mobile,
      imageOfPurchase,
      proofOfPurchaseNumber,
      dateOfPurchase: format(dateOfPurchase, 'yyyy-MM-dd'),
      consentAdult,
      consentRegulations,
      consentClause,
      consentCommunication,
      consentExclusion,
      nip,
    };
    this.register.emit({
      dto,
      form: this.form,
    });
  }

  public handleFileUpload(file?: File): void {
    const tenMbInBytes = 10485760;
    if (!file) {
      return;
    }
    const mimeType = file.type;
    if (
      mimeType !== 'image/png' &&
      mimeType !== 'image/jpg' &&
      mimeType !== 'image/jpeg'
    ) {
      this._toastrService.error(
        'Proszę wybrać plik w odpowiednim formacie.',
        'Błąd',
      );
      return;
    } else if (file.size > tenMbInBytes) {
      this._toastrService.error(
        'Wybrany plik nie może przekraczać 10MB.',
        'Błąd',
      );
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      if (this.imageResult && e.target?.result) {
        this._renderer2.setProperty(
          this.imageResult?.nativeElement,
          'src',
          e.target?.result,
        );
      }
    };
    this.f.imageOfPurchase.setValue(file);
    this.selectedFile.set(file);
  }

  public deleteSelectedFile(event: Event, filesInput: HTMLInputElement): void {
    event.preventDefault();
    this.selectedFile.set(null);
    this.f.imageOfPurchase.setValue(null);
    filesInput.value = '';
  }

  public onDropFile(event: DragEvent): void {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (!file) {
      return;
    }
    this.handleFileUpload(file);
  }

  public onDragOver(event: DragEvent): void {
    this.isDragOver.set(true);
    event.preventDefault();
  }

  public onDragLeave(_: DragEvent): void {
    this.isDragOver.set(false);
  }

  public onDisplayHintPopup(): void {
    this._ngbModal.open(ReceiptDialogComponent, {
      centered: true,
      keyboard: true,
      backdrop: true,
      container: 'body',
    });
  }
}
